import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getMapGrid, putMapGrid, rebuildMapBuild, revertMapGrid, type MapGrid } from "../api/rest";
import {
  parsePgm, encodePgm, paintDisk, fillOutsideRect, simplifyWalls,
  straightenToLine, uniformThickness, OCCUPIED, UNKNOWN, FREE,
  clampZoom, zoomBy, MIN_ZOOM, MAX_ZOOM, type Pgm,
} from "./mapGrid";
import { EdIcon } from "./MapEditorIcons";
import css from "./MapGridEditor.module.css";

type Tool = "unknown" | "free" | "wall" | "rect" | "line";

const ZOOM_STEP = 1.25;
const PAN_STEP = 96; // px scrolled per D-pad click
const LINE_CLEAR_MARGIN = 4; // extra cells cleared each side of a straightened wall

/** Cell value a brush paints: wall (occupied), unknown, or free floor. */
function paintVal(tool: Tool): number {
  return tool === "unknown" ? UNKNOWN : tool === "wall" ? OCCUPIED : FREE;
}

function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/**
 * 2D occupancy cleanup (ADR 102): brush stray scan noise to livre/desconhecido or
 * crop to a rectangle, then re-extrude the schematic GLB on the edge. Cell values
 * only — the raster size and the map frame never change, so poses stay valid.
 *
 * Thin query wrapper: the editor itself is keyed on dataUpdatedAt, so every fresh
 * fetch (load / "Restaurar do servidor" / post-crop refetch) remounts it with all
 * local state (raster, undo, dirty, zoom) reset atomically. Two reasons this is a
 * remount and not an in-place reset (ADR 136): TanStack's structural sharing keeps
 * the same data identity when a refetch returns unchanged bytes (an identity-keyed
 * reset silently skips), and the editor's paint path issues continuous-priority
 * setState from pointermove — React can rebase one of those after an in-place reset
 * commit, resurrecting `dirty` (observed live: reset committed, then save re-enabled).
 */
export function MapGridEditor({ buildId, onRebuildStarted }: {
  buildId: string;
  onRebuildStarted: () => void;
}) {
  const { t } = useTranslation();
  const grid = useQuery({
    queryKey: ["mapGrid", buildId],
    queryFn: () => getMapGrid(buildId),
    staleTime: Infinity,
  });

  if (grid.isLoading) return <div className={css.loading}>{t("common.loading")}</div>;
  if (grid.isError || !grid.data) return <div className={css.error}>{t("mapping.grid.loadError")}</div>;

  return (
    <EditorSurface key={grid.dataUpdatedAt} data={grid.data} buildId={buildId}
                   onRebuildStarted={onRebuildStarted} onReload={() => { void grid.refetch(); }} />
  );
}

function EditorSurface({ data, buildId, onRebuildStarted, onReload }: {
  data: MapGrid;
  buildId: string;
  onRebuildStarted: () => void;
  onReload: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const wrapRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  // Lazy parse on first render — the surface lives exactly as long as one fetched raster.
  const pgmRef = useRef<Pgm | null>(null);
  if (pgmRef.current === null) pgmRef.current = parsePgm(base64ToBytes(data.pgm));
  const undoRef = useRef<Uint8Array[]>([]);
  const dragRef = useRef<{ x: number; y: number } | null>(null);
  const hoverRef = useRef<{ x: number; y: number } | null>(null);
  const pendingScrollRef = useRef<{ left: number; top: number } | null>(null);
  const [tool, setTool] = useState<Tool>("unknown");
  const [brush, setBrush] = useState(3);
  const [dirty, setDirty] = useState(false);
  const [undoDepth, setUndoDepth] = useState(0);
  const [zoom, setZoom] = useState(1);
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom; // latest zoom for the non-passive wheel listener

  /** Strokes in "difference" blend so the same colour reads on occupied
   * (black), free (white) and unknown (grey) cells alike. */
  function strokeAdaptive(ctx: CanvasRenderingContext2D, lineWidth: number, path: () => void) {
    ctx.save();
    ctx.globalCompositeOperation = "difference";
    ctx.strokeStyle = "#ffffff";
    ctx.fillStyle = "#ffffff";
    ctx.lineWidth = lineWidth;
    ctx.beginPath();
    path();
    ctx.stroke();
    ctx.restore();
  }

  function draw(preview?: { kind: "rect" | "line"; x0: number; y0: number; x1: number; y1: number }) {
    const canvas = canvasRef.current;
    const p = pgmRef.current;
    if (!canvas || !p) return;
    canvas.width = p.width;
    canvas.height = p.height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const img = ctx.createImageData(p.width, p.height);
    for (let i = 0; i < p.data.length; i++) {
      const v = p.data[i];
      img.data[i * 4] = v;
      img.data[i * 4 + 1] = v;
      img.data[i * 4 + 2] = v;
      img.data[i * 4 + 3] = 255;
    }
    ctx.putImageData(img, 0, 0);
    const lineWidth = Math.max(1, Math.round(p.width / 300));
    if (preview?.kind === "rect") {
      strokeAdaptive(ctx, lineWidth, () => ctx.rect(
        Math.min(preview.x0, preview.x1), Math.min(preview.y0, preview.y1),
        Math.abs(preview.x1 - preview.x0), Math.abs(preview.y1 - preview.y0)));
    } else if (preview?.kind === "line") {
      strokeAdaptive(ctx, Math.max(lineWidth, brush), () => {
        ctx.moveTo(preview.x0 + 0.5, preview.y0 + 0.5);
        ctx.lineTo(preview.x1 + 0.5, preview.y1 + 0.5);
      });
    } else if (tool !== "rect" && hoverRef.current) {
      const { x, y } = hoverRef.current;
      strokeAdaptive(ctx, lineWidth, () => ctx.arc(x + 0.5, y + 0.5, brush, 0, Math.PI * 2));
      ctx.save();
      ctx.globalCompositeOperation = "difference";
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(x, y, 1, 1);
      ctx.restore();
    }
  }

  // First paint on mount + keep the brush ring in sync when the brush size or the
  // active tool changes without waiting for the next pointer move.
  useEffect(() => { draw(); }, [brush, tool]);

  useEffect(() => {
    const wrap = wrapRef.current;
    const pending = pendingScrollRef.current;
    if (wrap && pending) {
      wrap.scrollLeft = pending.left;
      wrap.scrollTop = pending.top;
      pendingScrollRef.current = null;
    }
  }, [zoom]);

  // Keep the content point under `focal` (a point in the scroll viewport, default
  // its centre) pinned while the canvas grows/shrinks — the scroll is applied
  // after the re-render paints the new width, via the [zoom] effect above.
  function applyZoom(next: number, focal?: { x: number; y: number }) {
    const wrap = wrapRef.current;
    const clamped = clampZoom(next);
    if (wrap && clamped !== zoom) {
      const fx = focal ? focal.x : wrap.clientWidth / 2;
      const fy = focal ? focal.y : wrap.clientHeight / 2;
      const ratio = clamped / zoom;
      pendingScrollRef.current = {
        left: (wrap.scrollLeft + fx) * ratio - fx,
        top: (wrap.scrollTop + fy) * ratio - fy,
      };
    }
    setZoom(clamped);
  }

  // React registers onWheel as a PASSIVE listener, so e.preventDefault() there is
  // silently ignored and the page scrolls while we zoom. Attach our own
  // non-passive listener so preventDefault sticks. Mount-only: the surface always
  // renders the scroller (the wrapper handled loading/error before mounting us).
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const onWheelNative = (e: WheelEvent) => {
      if (!e.ctrlKey) return; // plain scroll/trackpad pans natively; ctrl+wheel (incl. trackpad pinch) zooms
      e.preventDefault();
      const cur = zoomRef.current;
      const next = clampZoom(zoomBy(cur, Math.exp(-e.deltaY * 0.0015)));
      if (next === cur) return;
      const r = el.getBoundingClientRect();
      const fx = e.clientX - r.left, fy = e.clientY - r.top;
      const ratio = next / cur;
      pendingScrollRef.current = {
        left: (el.scrollLeft + fx) * ratio - fx,
        top: (el.scrollTop + fy) * ratio - fy,
      };
      setZoom(next);
    };
    el.addEventListener("wheel", onWheelNative, { passive: false });
    return () => el.removeEventListener("wheel", onWheelNative);
  }, []);

  function pan(dx: number, dy: number) {
    wrapRef.current?.scrollBy({ left: dx, top: dy, behavior: "smooth" });
  }

  function cell(e: React.PointerEvent): { x: number; y: number } {
    const c = canvasRef.current!;
    const p = pgmRef.current!;
    const r = c.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(p.width - 1, Math.floor((e.clientX - r.left) * (p.width / r.width)))),
      y: Math.max(0, Math.min(p.height - 1, Math.floor((e.clientY - r.top) * (p.height / r.height)))),
    };
  }

  function snapshot() {
    const p = pgmRef.current;
    if (!p) return;
    undoRef.current.push(p.data.slice());
    if (undoRef.current.length > 20) undoRef.current.shift();
    setUndoDepth(undoRef.current.length);
  }

  function onPointerDown(e: React.PointerEvent) {
    if (!pgmRef.current) return;
    (e.target as Element).setPointerCapture(e.pointerId);
    snapshot();
    const c = cell(e);
    dragRef.current = c;
    hoverRef.current = c;
    if (tool !== "rect" && tool !== "line") {
      paintDisk(pgmRef.current, c.x, c.y, brush, paintVal(tool));
      setDirty(true);
      draw();
    }
  }

  function onPointerMove(e: React.PointerEvent) {
    if (!pgmRef.current) return;
    const c = cell(e);
    hoverRef.current = c;
    if (!dragRef.current) { draw(); return; }
    if (tool === "rect") {
      draw({ kind: "rect", x0: dragRef.current.x, y0: dragRef.current.y, x1: c.x, y1: c.y });
    } else if (tool === "line") {
      draw({ kind: "line", x0: dragRef.current.x, y0: dragRef.current.y, x1: c.x, y1: c.y });
    } else {
      paintDisk(pgmRef.current, c.x, c.y, brush, paintVal(tool));
      setDirty(true);
      draw();
    }
  }

  function onPointerLeave() {
    hoverRef.current = null;
    if (!dragRef.current) draw();
  }

  function onPointerUp(e: React.PointerEvent) {
    if (!dragRef.current || !pgmRef.current) return;
    const c = cell(e);
    if (tool === "rect") {
      fillOutsideRect(pgmRef.current, dragRef.current.x, dragRef.current.y, c.x, c.y, UNKNOWN);
      setDirty(true);
      draw();
    } else if (tool === "line") {
      straightenToLine(pgmRef.current, dragRef.current.x, dragRef.current.y, c.x, c.y,
                       Math.max(1, brush), brush + LINE_CLEAR_MARGIN);
      setDirty(true);
      draw();
    }
    dragRef.current = null;
  }

  function normalize() {
    if (!pgmRef.current) return;
    snapshot();
    uniformThickness(pgmRef.current, 1);
    setDirty(true);
    draw();
  }

  function undo() {
    const prev = undoRef.current.pop();
    if (!prev || !pgmRef.current) return;
    pgmRef.current.data.set(prev);
    setUndoDepth(undoRef.current.length);
    setDirty(true);
    draw();
  }

  function simplify() {
    if (!pgmRef.current) return;
    snapshot();
    simplifyWalls(pgmRef.current);
    setDirty(true);
    draw();
  }

  const apply = useMutation({
    mutationFn: async () => {
      await putMapGrid(buildId, encodePgm(pgmRef.current!));
      return rebuildMapBuild(buildId);
    },
    onSuccess: () => {
      // The server may have auto-cropped: the next editor open must refetch.
      void qc.invalidateQueries({ queryKey: ["mapGrid", buildId] });
      onRebuildStarted();
    },
  });

  const revert = useMutation({
    mutationFn: async () => {
      await revertMapGrid(buildId);
      return rebuildMapBuild(buildId);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["mapGrid", buildId] });
      onRebuildStarted();
    },
  });

  const tools: { id: Tool; icon: string }[] = [
    { id: "unknown", icon: "unknown" },
    { id: "free", icon: "free" },
    { id: "wall", icon: "wall" },
    { id: "line", icon: "straighten" },
    { id: "rect", icon: "crop" },
  ];

  return (
    <div className={css.editor}>
      <div className={css.palette} role="toolbar" aria-label={t("mapping.grid.paletteLabel")}>
        <div className={css.group}>
          {tools.map(({ id, icon }) => (
            <button key={id} type="button"
                    className={tool === id ? css.iconBtnActive : css.iconBtn}
                    aria-label={t(`mapping.grid.tip.${id}`)} aria-pressed={tool === id}
                    onClick={() => setTool(id)}>
              <EdIcon name={icon} />
            </button>
          ))}
        </div>
        <div className={css.divider} />
        <div className={css.group}>
          <button type="button" className={css.iconBtn} aria-label={t("mapping.grid.tip.simplify")} onClick={simplify}>
            <EdIcon name="simplify" />
          </button>
          <button type="button" className={css.iconBtn} aria-label={t("mapping.grid.tip.uniform")} onClick={normalize}>
            <EdIcon name="uniform" />
          </button>
        </div>
        <div className={css.divider} />
        <label className={css.brushGroup} aria-label={t("mapping.grid.tip.brush")}>
          <EdIcon name="brush" />
          <input type="range" min={1} max={12} value={brush}
                 aria-label={t("mapping.grid.tip.brush")}
                 onChange={(e) => setBrush(Number(e.target.value))} />
        </label>
        <div className={css.spacer} />
        <div className={css.group}>
          <button type="button" className={css.iconBtn} aria-label={t("mapping.grid.tip.undo")}
                  onClick={undo} disabled={undoDepth === 0}>
            <EdIcon name="undo" />
          </button>
          <button type="button" className={css.iconBtn} aria-label={t("mapping.grid.tip.reload")}
                  onClick={onReload}>
            <EdIcon name="reload" />
          </button>
          <span className={css.iconBtn} role="note" tabIndex={0} aria-label={t("mapping.grid.tip.save")}>
            <EdIcon name="info" />
          </span>
        </div>
      </div>
      {/* .frame stays put; only .canvasWrap scrolls, so the controls anchored to
          the frame never scroll away when the canvas is zoomed. */}
      <div className={css.frame}>
        <div className={css.canvasWrap} ref={wrapRef}>
          <canvas ref={canvasRef} className={css.canvas}
                  style={{ width: `${zoom * 100}%`, cursor: tool === "rect" ? "crosshair" : "none" }}
                  onPointerDown={onPointerDown} onPointerMove={onPointerMove}
                  onPointerUp={onPointerUp} onPointerLeave={onPointerLeave} />
        </div>
        <div className={css.zoomControls}>
          <button type="button" className={css.zoomBtn} aria-label={t("mapping.grid.zoomIn")}
                  disabled={zoom >= MAX_ZOOM} onClick={() => applyZoom(zoomBy(zoom, ZOOM_STEP))}>
            +
          </button>
          <button type="button" className={css.zoomReadout} aria-label={t("mapping.grid.zoomReset")}
                  disabled={zoom === MIN_ZOOM} onClick={() => applyZoom(MIN_ZOOM)}>
            {Math.round(zoom * 100)}%
          </button>
          <button type="button" className={css.zoomBtn} aria-label={t("mapping.grid.zoomOut")}
                  disabled={zoom <= MIN_ZOOM} onClick={() => applyZoom(zoomBy(zoom, 1 / ZOOM_STEP))}>
            −
          </button>
        </div>
        <div className={css.panControls}>
          <button type="button" className={`${css.panBtn} ${css.panUp}`} aria-label={t("mapping.grid.panUp")}
                  onClick={() => pan(0, -PAN_STEP)}>▲</button>
          <button type="button" className={`${css.panBtn} ${css.panLeft}`} aria-label={t("mapping.grid.panLeft")}
                  onClick={() => pan(-PAN_STEP, 0)}>◀</button>
          <button type="button" className={`${css.panBtn} ${css.panRight}`} aria-label={t("mapping.grid.panRight")}
                  onClick={() => pan(PAN_STEP, 0)}>▶</button>
          <button type="button" className={`${css.panBtn} ${css.panDown}`} aria-label={t("mapping.grid.panDown")}
                  onClick={() => pan(0, PAN_STEP)}>▼</button>
        </div>
      </div>
      {(apply.isError || revert.isError) && (
        <div className={css.error}>{((apply.error || revert.error) as Error).message}</div>
      )}
      <div className={css.actions}>
        {data.edited && (
          <button type="button" className={css.tool}
                  disabled={apply.isPending || revert.isPending}
                  onClick={() => revert.mutate()}>
            {t("mapping.grid.revert")}
          </button>
        )}
        <button type="button" className={css.applyBtn}
                disabled={!dirty || apply.isPending || revert.isPending}
                onClick={() => apply.mutate()}>
          {apply.isPending ? t("mapping.grid.applying") : t("mapping.grid.apply")}
        </button>
      </div>
    </div>
  );
}
